import { useMemo } from 'react';

import {
  PREDEFINED_SOURCES,
  getSourceCategory,
  getSourceDisplayName,
  type SourceItem,
} from '../../../../contracts';
import { useSourceCatalogue } from '../../../../data/injected';

// `SourceItem` MOVED TO `/contracts` rather than staying declared here:
// `source-lists/isRegionMatch.ts` is a pure predicate over it and the inert
// door may not reach a hook module. Re-exported so this module still reads as
// the shape's home to anyone following the pipeline.
export type { SourceItem };

/**
 * Minimum authoritativeness score for a source to appear in search and selection UIs.
 *
 * **Engineering decision**: Sources scoring below this threshold are excluded entirely
 * from the create-collection modal and source search. Raise this value to show only
 * higher-quality sources; lower it to widen the pool. Sources with a null/undefined
 * score from the backend are treated as 100 (shown by default).
 *
 * Current value: 50.
 */
const MIN_AUTHORITATIVENESS_SCORE = 50;

/**
 * Shared hook that provides a merged, deduplicated, filtered source list
 * from the API sources and predefined sources.
 *
 * Used by DomainInput, SourceSearchInput, and useDomainDisplayNames to avoid
 * duplicating the source data pipeline.
 */
export function useAllSources(minScore: number = MIN_AUTHORITATIVENESS_SCORE): {
  allSources: SourceItem[];
  isLoading: boolean;
} {
  const { data: apiSources, isLoading } = useSourceCatalogue();

  const allSources = useMemo((): SourceItem[] => {
    const sources: SourceItem[] = [];
    const seenDomains = new Set<string>();

    if (apiSources) {
      for (const source of apiSources) {
        const score = source.authoritativeness_score ?? 100;
        if (score < minScore) continue;
        if (!seenDomains.has(source.domain)) {
          seenDomains.add(source.domain);
          const label = getSourceDisplayName(source);
          sources.push({
            domain: source.domain,
            domainLower: source.domain.toLowerCase(),
            label,
            labelLower: label.toLowerCase(),
            category: getSourceCategory(source),
            authoritativenessScore: score,
            regions: source.regions ?? [],
          });
        }
      }
    }

    for (const source of PREDEFINED_SOURCES) {
      if (!seenDomains.has(source.domain)) {
        seenDomains.add(source.domain);
        sources.push({
          domain: source.domain,
          domainLower: source.domain.toLowerCase(),
          label: source.label,
          labelLower: source.label.toLowerCase(),
          category: source.category,
          authoritativenessScore: 100,
          regions: [],
        });
      }
    }

    return sources;
  }, [apiSources, minScore]);

  return { allSources, isLoading };
}
